import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useApi } from '../../context/ApiContext';

export default function TenantProfile(){
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminGetTenant, uploadFile, adminAddTenantDocument, adminSetTenantStatus, adminRequestTenantPayment, adminMarkTenantLeaseSigned, adminCompleteTenantMoveIn } = useApi();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const TabButton = ({ id, children }) => (
    <button
      className={`px-3 py-2 text-sm border-b-2 ${tab===id ? 'border-orange-600 text-orange-700' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
      onClick={()=>setTab(id)}
      type="button"
    >{children}</button>
  );
  const [docName, setDocName] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [sendingPayment, setSendingPayment] = useState(false);
  const [markingSigned, setMarkingSigned] = useState(false);
  const [movingIn, setMovingIn] = useState(false);
  const [moveInDate, setMoveInDate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true); setError('');
        const data = await adminGetTenant(id);
        setItem(data || null);
      } catch(e){ setError(e?.message || 'Failed to load tenant'); }
      finally { setLoading(false); }
    })();
  }, [id, adminGetTenant]);

  useEffect(() => {
    if (!item) return;
    setMoveInDate(item.moveInDate || new Date().toISOString().slice(0, 10));
  }, [item]);

  if (loading) return <div className="p-6">Loading…</div>;
  if (error || !item) return (
    <div className="p-6">
      <div className="mb-3"><button className="inline-flex items-center gap-2" onClick={()=>navigate(-1)}><ArrowLeft className="w-4 h-4"/> Back</button></div>
      <div className="text-red-700">{error || 'Not found'}</div>
    </div>
  );

  const docs = item.documents || [];
  const avatarUrl = (() => {
    if (item.profilePhoto) return item.profilePhoto;
    const byName = docs.find(d => typeof d?.name === 'string' && /profile/i.test(d.name) && d.url);
    if (byName?.url) return byName.url;
    const byExt = docs.find(d => (d?.fileName || d?.url || '').toLowerCase().match(/\.(jpg|jpeg|png|webp)$/) && d.url);
    return byExt?.url || '';
  })();
  const canCompleteMoveIn = (item.status || '').toLowerCase() === 'upcoming' && ['signed', 'active'].includes((item.lease?.status || '').toLowerCase());
  const canMarkLeaseSigned = (item.status || '').toLowerCase() === 'upcoming' && ['draft', 'pending_signature'].includes((item.lease?.status || '').toLowerCase());

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between">
        <button className="inline-flex items-center gap-2" onClick={()=>navigate(-1)}><ArrowLeft className="w-4 h-4"/> Back</button>
        <div className="text-xs text-gray-600">ID: {item.id}</div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 md:p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            {avatarUrl ? (
              <img src={avatarUrl} alt={item.fullName || item.name || ''} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200" />
            )}
            <div>
              <div className="text-2xl font-bold">{item.fullName || item.name}</div>
              <div className="text-gray-700">{item.email} • {item.phone}</div>
              <div className="text-sm text-gray-600">Status: {item.status} • Lease: {item.lease?.status || '—'}</div>
              <div className="text-sm text-gray-600">Property: {item.propertySlug} • Unit: {item.unitId || '-'}</div>
              <div className="text-sm text-gray-600">Updated: {item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 border rounded"
              disabled={sendingPayment}
              onClick={async()=>{
                try {
                  setSendingPayment(true);
                  const res = await adminRequestTenantPayment({ tenantId: id });
                  alert(res?.message || 'Payment request email sent');
                } catch (e) {
                  alert(e?.message || 'Failed to send payment request');
                } finally {
                  setSendingPayment(false);
                }
              }}
            >{sendingPayment ? 'Sending…' : 'Request Payment'}</button>
            {canMarkLeaseSigned && (
              <button
                className="px-3 py-2 border rounded bg-gray-900 text-white disabled:opacity-50"
                disabled={markingSigned}
                onClick={async()=>{
                  try {
                    setMarkingSigned(true);
                    const updated = await adminMarkTenantLeaseSigned({ tenantId: id });
                    setItem(updated || null);
                    alert('Lease marked signed. Move-in can now be completed.');
                  } catch (e) {
                    alert(e?.message || 'Failed to mark lease signed');
                  } finally {
                    setMarkingSigned(false);
                  }
                }}
              >{markingSigned ? 'Updating…' : 'Mark Lease Signed'}</button>
            )}
            {canCompleteMoveIn && (
              <>
                <input
                  type="date"
                  className="px-3 py-2 border rounded"
                  value={moveInDate}
                  onChange={(e) => setMoveInDate(e.target.value)}
                  disabled={movingIn}
                />
                <button
                  className="px-3 py-2 border rounded bg-orange-600 text-white disabled:opacity-50"
                  disabled={movingIn}
                  onClick={async()=>{
                    try {
                      setMovingIn(true);
                      const updated = await adminCompleteTenantMoveIn({ tenantId: id, moveInDate: moveInDate || undefined });
                      setItem(updated || null);
                      alert('Move-in completed.');
                    } catch (e) {
                      alert(e?.message || 'Failed to complete move-in');
                    } finally {
                      setMovingIn(false);
                    }
                  }}
                >{movingIn ? 'Completing…' : 'Complete Move-in'}</button>
              </>
            )}
            <button
              className="px-3 py-2 border rounded"
              onClick={async()=>{
                const reason = window.prompt('Reason (optional):', 'Vacated');
                try { await adminSetTenantStatus({ tenantId: id, status: 'Inactive', reason: reason || '' }); alert('Moved to Inactive'); navigate('/admin/inactives'); } catch(e){ alert(e?.message||'Failed'); }
              }}
            >Move to Inactive</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b flex items-center gap-2">
          <TabButton id="overview">Overview</TabButton>
          <TabButton id="documents">Documents</TabButton>
          <TabButton id="timeline">Timeline</TabButton>
        </div>

        {/* Overview */}
        {tab==='overview' && (
          <div className="mt-4 grid md:grid-cols-2 gap-4">
            <section className="border rounded p-4">
              <h2 className="font-semibold mb-2">Overview</h2>
              <div className="text-sm text-gray-700">Property: {item.propertySlug} • Unit: {item.unitId || '-'}</div>
              <div className="text-sm text-gray-700">Lease: {item.lease?.status || '—'}</div>
              <div className="text-sm text-gray-700">Status: {item.status}</div>
              <div className="text-sm text-gray-700">Move-in date: {item.moveInDate || '—'}</div>
              {canMarkLeaseSigned && (
                <div className="mt-2 text-sm text-amber-700">Mark the lease signed first, then the move-in action will appear.</div>
              )}
            </section>
            <section className="border rounded p-4">
              <h2 className="font-semibold mb-2">Notes</h2>
              <div className="text-sm text-gray-500">Coming soon…</div>
            </section>
          </div>
        )}

        {/* Documents */}
        {tab==='documents' && (
          <section className="mt-4 border rounded p-4">
            <h2 className="font-semibold mb-2">Documents</h2>
            <div className="mb-3 flex items-center gap-2">
              <input className="border rounded px-2 py-1" placeholder="Document name" value={docName} onChange={(e)=>setDocName(e.target.value)} />
              <input type="file" onChange={(e)=>setDocFile(e.target.files?.[0]||null)} />
              <button type="button" disabled={saving || !docFile} className="px-3 py-1.5 border rounded disabled:opacity-50" onClick={async()=>{
                if (!docFile) return;
                try {
                  setSaving(true);
                  setUploadError('');
                  const up = await uploadFile(docFile, { folder: `tenants/${id}` });
                  await adminAddTenantDocument({ tenantId: id, document: { name: docName || docFile.name, fileName: docFile.name, url: up.url } });
                  const data = await adminGetTenant(id);
                  setItem(data || null);
                  setDocName(''); setDocFile(null);
                } catch (e) {
                  setUploadError(e?.message || 'Upload failed');
                  alert(e?.message || 'Upload failed');
                } finally { setSaving(false); }
              }}>Upload</button>
            </div>
            {uploadError && <div className="text-xs text-red-600 mb-2">{uploadError}</div>}
            <ul className="text-sm space-y-2">
              {docs.map((d, i) => (
                <li key={i} className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-medium">{d.name || 'Document'}</div>
                    <div className="text-xs text-gray-600 break-all">{d.fileName || d.url || ''}</div>
                  </div>
                  {d.url ? (
                    <div className="flex items-center gap-2">
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">View</a>
                      <a href={d.url} download className="text-gray-700 border px-2 py-1 rounded text-xs">Download</a>
                    </div>
                  ) : <span className="text-xs text-gray-500">No file</span>}
                </li>
              ))}
              {docs.length === 0 && (<li className="text-gray-500">No documents</li>)}
            </ul>
          </section>
        )}

        {/* Timeline */}
        {tab==='timeline' && (
          <section className="mt-4 border rounded p-4">
            <h2 className="font-semibold mb-2">Timeline</h2>
            <ul className="text-sm">
              {(item.events || []).slice().reverse().map(ev => (
                <li key={ev.id} className="py-1"><span className="text-gray-500">{new Date(ev.at).toLocaleString()}:</span> {ev.label}</li>
              ))}
              {(item.events || []).length === 0 && <li className="text-gray-500">No events</li>}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
